import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ShieldCheck } from 'lucide-react';
import { useGDLData } from './useGDLData';
import { ParticleReveal, CrossSellModal } from './AtomParticleReveal';
import { SuccessScreen } from './GDLComponents';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function GDLLoanApply() {
  const nav = useNavigate();
  const { csId, wallet, refresh } = useGDLData();
  const [stage, setStage] = useState('form');  // form | offer | success
  const [amount, setAmount] = useState(150000);
  const [offer, setOffer] = useState(null);
  const [result, setResult] = useState(null);
  const [crosssell, setCrosssell] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Pre-fill with seed-data-based defaults from wallet
  useEffect(() => {
    if (wallet?.civil_servant?.monthly_salary) {
      const def = Math.round(wallet.civil_servant.monthly_salary * 1.5 / 10000) * 10000;
      setAmount(def);
    }
  }, [wallet]);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await axios.post(
        `${API}/api/demo/gdl/loan/apply/${csId}`,
        { requested_amount: amount },
        { withCredentials: true }
      );
      setOffer(r.data);
      setStage('offer');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not request loan');
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/demo/gdl/loan/accept/${offer.id}`,
        {}, { withCredentials: true }
      );
      setResult(r.data);
      setCrosssell(r.data.crosssell || null);
      setStage('success');
      refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not book loan');
    } finally {
      setBusy(false);
    }
  };

  const acceptCrosssell = async (s) => {
    try {
      const p = s.cta_payload || {};
      await axios.post(
        `${API}/api/demo/gdl/atom/vault-lock/${csId}`,
        { amount: p.amount, days: p.days || 30, rate: p.rate || 0.14,
          label: 'Loan-proceeds vault lock' },
        { withCredentials: true }
      );
      setCrosssell(null);
      refresh();
    } catch (e) {
      setCrosssell(null);
    }
  };

  const cs = wallet?.civil_servant;
  const existingMonthly = wallet?.loan?.monthly_repayment || 0;

  if (stage === 'success') {
    return (
      <>
        <SuccessScreen
          title="Loan disbursed"
          subtitle={`${'\u20A6'}${result.amount.toLocaleString()} added to your wallet.`}
          reference={result.reference}
          buttonLabel="Back to home"
          onDone={() => nav('/demo/gdl/dashboard')}
        />
        <CrossSellModal
          suggestion={crosssell}
          onAccept={acceptCrosssell}
          onDismiss={() => setCrosssell(null)}
        />
      </>
    );
  }

  return (
    <div style={{ padding: '12px 16px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <button onClick={() => nav(-1)} data-testid="loan-back" style={{
        display: 'flex', alignItems: 'center', gap: 4,
        background: 'transparent', border: 'none',
        color: '#8E2233', fontSize: 14, fontWeight: 600, cursor: 'pointer',
        padding: 0, marginBottom: 4,
      }}>
        <ChevronLeft size={18}/> Back
      </button>

      {stage === 'form' && (
        <>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#2D0A10', marginBottom: 4 }}>
              Apply for a GDL Loan
            </div>
            <div style={{ fontSize: 13, color: '#A07078' }}>
              Civil-servant rate. Repayment is auto-deducted from your salary.
            </div>
          </div>

          {/* Pre-filled commitments */}
          <div data-testid="loan-prefill" style={{
            background: 'rgba(176,49,64,0.04)', borderRadius: 16,
            padding: '14px 16px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#8E2233',
                            textTransform: 'uppercase', letterSpacing: '0.06em',
                            marginBottom: 10 }}>
              From your record
            </div>
            <Row label="Civil servant" value={cs?.full_name} />
            <Row label="Ministry" value={`${cs?.state || ''} · ${cs?.ministry || ''}`} />
            <Row label="Monthly salary" value={`${'\u20A6'}${(cs?.monthly_salary || 0).toLocaleString()}`} />
            <Row label="Existing monthly repayment"
                  value={existingMonthly ? `${'\u20A6'}${existingMonthly.toLocaleString()}` : 'None'} />
          </div>

          {/* Amount */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#2D0A10', marginBottom: 8 }}>
              How much do you need?
            </div>
            <input data-testid="loan-amount-input" type="number" value={amount}
              onChange={e => setAmount(Number(e.target.value))}
              min={25000} step={5000}
              style={{
                width: '100%', padding: '14px 16px',
                fontSize: 22, fontWeight: 700, color: '#2D0A10',
                border: '2px solid rgba(176,49,64,0.15)', borderRadius: 14,
                background: '#fff',
              }}
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {[50000, 100000, 200000, 300000].map(p => (
                <button key={p} onClick={() => setAmount(p)}
                  data-testid={`loan-quick-${p}`}
                  style={{
                    padding: '6px 12px', borderRadius: 999,
                    border: '1px solid rgba(176,49,64,0.2)',
                    background: amount === p ? '#B03140' : '#fff',
                    color: amount === p ? '#fff' : '#8E2233',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}>
                  {'\u20A6'}{p.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          {error && <div style={{ color: '#DC2626', fontSize: 13 }}>{error}</div>}

          <button onClick={submit} disabled={busy || !amount}
            data-testid="loan-submit-btn" style={{
            padding: '14px 16px',
            background: '#B03140', color: '#fff',
            border: 'none', borderRadius: 14,
            fontSize: 15, fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(176,49,64,0.25)',
            opacity: busy ? 0.6 : 1,
          }}>
            {busy ? 'Drawing up your offer…' : 'See my pre-approved offer'}
          </button>
        </>
      )}

      {stage === 'offer' && offer && (
        <>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#2D0A10' }}>
              You're pre-approved
            </div>
            <div style={{ fontSize: 13, color: '#A07078', marginTop: 4 }}>
              Approved by {offer.officer_name}
            </div>
          </div>

          {/* Offer card */}
          <div data-testid="loan-offer" style={{
            background: 'linear-gradient(145deg, #C43B4D, #B03140 50%, #8E2233)',
            color: '#fff', borderRadius: 20, padding: '20px 22px',
            boxShadow: '0 8px 28px rgba(176,49,64,0.3)',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: -30, right: -30, width: 110, height: 110,
                            borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
            <div style={{ position: 'relative' }}>
              <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.65,
                              textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Approved amount
              </div>
              <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em',
                              marginTop: 4 }}>
                {'\u20A6'}{offer.approved_amount.toLocaleString()}
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 14 }}>
                <div style={{ flex: 1, background: 'rgba(255,255,255,0.1)',
                                borderRadius: 12, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, opacity: 0.7, fontWeight: 600,
                                  textTransform: 'uppercase' }}>Monthly</div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>
                    {'\u20A6'}{offer.monthly_repayment.toLocaleString()}
                  </div>
                </div>
                <div style={{ flex: 1, background: 'rgba(255,255,255,0.1)',
                                borderRadius: 12, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, opacity: 0.7, fontWeight: 600,
                                  textTransform: 'uppercase' }}>Tenure</div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>
                    {offer.tenure_months} months
                  </div>
                </div>
                <div style={{ flex: 1, background: 'rgba(255,255,255,0.1)',
                                borderRadius: 12, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, opacity: 0.7, fontWeight: 600,
                                  textTransform: 'uppercase' }}>Rate</div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>
                    {(offer.annual_rate * 100).toFixed(0)}% pa
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ParticleReveal
            variant="Credit memo"
            headline="Why this offer was approved"
            body={offer.rationale}
            particles={offer.particles}
            openByDefault={true}
            testid="loan-credit-memo"
          />

          {error && <div style={{ color: '#DC2626', fontSize: 13 }}>{error}</div>}

          <button onClick={accept} disabled={busy}
            data-testid="loan-accept-btn" style={{
            padding: '14px 16px',
            background: '#B03140', color: '#fff',
            border: 'none', borderRadius: 14,
            fontSize: 15, fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(176,49,64,0.25)',
            opacity: busy ? 0.6 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            <ShieldCheck size={18}/>
            {busy ? 'Disbursing…' : `Accept & receive ${'\u20A6'}${offer.approved_amount.toLocaleString()}`}
          </button>
          <button onClick={() => setStage('form')} data-testid="loan-decline-btn" style={{
            padding: '12px 16px', background: 'transparent',
            color: '#8E2233', border: '1px solid rgba(176,49,64,0.2)',
            borderRadius: 14, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>
            Not now
          </button>
        </>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between',
                    fontSize: 13, padding: '4px 0' }}>
      <span style={{ color: '#A07078' }}>{label}</span>
      <span style={{ color: '#2D0A10', fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}
